import { Auth } from 'aws-amplify';
import { get } from 'lodash';
import posthog from 'posthog-js';

import { AWS_COGNITO_RESPONSE, LOCALSTORAGE_SSO_HANDOFF_SESSION, TEMP_PASSWORD_EXPIRED_ERROR } from '../util/constants';
import { detectClockSkew } from '../util/detectClockSkew';
import getAppVersion from '../util/getAppVersion';

export const logIn = async ({ email, password, completeSignup = false }) => {
  try {
    const response = await Auth.signIn(email, password);
    const ChallengeName = get(response, 'challengeName');
    if (ChallengeName === 'NEW_PASSWORD_REQUIRED') {
      return {
        status: AWS_COGNITO_RESPONSE.RESET_PASSWORD_REQUIRED,
      };
    }

    // Hub-owned Cognito session includes a refresh token — clear any prior
    // student-hub SSO handoff marker so expiry can force-refresh normally.
    localStorage.removeItem(LOCALSTORAGE_SSO_HANDOFF_SESSION);

    return response;
  } catch (e) {
    const code = get(e, 'code');
    if (code === 'NotAuthorizedException' && completeSignup) {
      const message = get(e, 'message');
      // Note, we have to reply on checking error message since the response code is not specific enough
      if (message === TEMP_PASSWORD_EXPIRED_ERROR) {
        throw new Error(message);
      }

      return {
        status: AWS_COGNITO_RESPONSE.NOT_AUTHORIZED_EXCEPTION,
      };
    }
    throw e;
  }
};

export const completeSignupByChangingPwd = async ({ email, tempPassword, newPassword }) => {
  const user = await Auth.signIn(email, tempPassword);
  const response = await Auth.completeNewPassword(user, newPassword);
  // Password completion yields a hub-owned session with a refresh token.
  localStorage.removeItem(LOCALSTORAGE_SSO_HANDOFF_SESSION);
  return response;
};

/**
 * Force a Cognito refresh-token round-trip. Local JWT TTL checks alone miss
 * GlobalSignOut (e.g. logout on student-hub) while the access token is still valid.
 *
 * Student-hub SSO handoff sessions intentionally omit a refresh token — trust
 * local JWT validity only. Hub-native sessions always refresh so expiry and
 * revocation are handled via the stored refresh token.
 */
export const forceRefreshSession = async () => {
  const user = await Auth.currentAuthenticatedUser();
  const session = await Auth.currentSession();
  const isSsoHandoffSession = localStorage.getItem(LOCALSTORAGE_SSO_HANDOFF_SESSION) === 'true';

  if (isSsoHandoffSession) {
    return session;
  }

  const refreshToken = session.getRefreshToken();
  const refreshTokenValue = refreshToken?.getToken?.();
  if (!refreshTokenValue) {
    throw new Error('No refresh token');
  }

  return new Promise((resolve, reject) => {
    user.refreshSession(refreshToken, (err, newSession) => {
      if (err) {
        reject(err);
        return;
      }
      resolve(newSession);
    });
  });
};

export const getCurrentCredentials = async () => {
  const user = await Auth.currentAuthenticatedUser();

  const detectedClockSkew = await detectClockSkew(4);
  console.log('[debug] clock skew and drift', { detectedClockSkew, drift: user.signInUserSession.clockDrift });

  if (detectedClockSkew) {
    throw new Error('Clock skew detected');
  }

  // User Pool session only — no Identity Pool (IAM) credentials required.
  // API auth uses the Cognito ID token as a Bearer JWT.
  // Hub / staff: force-refresh via stored refresh token (expiry + GlobalSignOut).
  // Student-hub SSO handoff: no refresh token — validate local JWTs until expiry.
  const session = await forceRefreshSession();
  if (!session?.isValid()) {
    throw new Error('No valid session');
  }

  const idToken = session.getIdToken();
  return {
    accessKeyId: null,
    secretAccessKey: null,
    sessionToken: idToken.getJwtToken(),
    expiration: new Date(idToken.getExpiration() * 1000).toISOString(),
    authenticated: true,
  };
};

export const getCurrentUserInfo = async () => {
  const user = await Auth.currentUserInfo();
  console.log('User', user);
  const customerId = get(user, 'attributes.custom:customerId', null);
  if (customerId) {
    posthog.identify(customerId, { $customer_id: customerId, $chub_version: getAppVersion() });
  }
  return user;
};

export const signOut = async () => {
  const res = await Auth.signOut({ global: true });
  posthog.reset();
  return res;
};

export const forgotPassword = async (username) => {
  const res = await Auth.forgotPassword(username);
  return res;
};

export const forgotPasswordSubmit = async ({ username, code, password }) => {
  const res = await Auth.forgotPasswordSubmit(username, code, password);
  return res;
};
